import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Phone, Send, Store, Truck, Bus, UtensilsCrossed, Sprout } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageHero } from "@/components/site/PageHero";
import { SeasonalityChart } from "@/components/site/wheretobuy/SeasonalityChart";
import { Reveal, Kicker, WfButton, ImageWithCredit } from "@/components/site/primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  WHOLESALE_HERO,
  WHOLESALE_INTRO,
  WHOLESALE_CHANNELS,
  WHOLESALE_SEASONALITY,
  WHOLESALE_BENEFITS,
  WHOLESALE_BUYER_TYPES,
  WHOLESALE_CONTACT,
} from "@/content/wholesale";
import { cn } from "@/lib/utils";

const CHANNEL_ICON = { grocery: Store, "food-hubs": Truck, mobile: Bus, restaurants: UtensilsCrossed };

/* ---------------- Intro ---------------- */
const IntroSection = () => (
  <section id="wholesale-intro" aria-labelledby="wholesale-intro-heading" className="wf-section bg-[var(--wf-cream)]">
    <div className="wf-container">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <Kicker>{WHOLESALE_INTRO.eyebrow}</Kicker>
          <h2 id="wholesale-intro-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">{WHOLESALE_INTRO.title}</h2>
          <div className="mt-5 space-y-4">
            {WHOLESALE_INTRO.paragraphs.map((p, i) => (<p key={i} className="max-w-[60ch] text-base leading-7 text-[var(--wf-soil)]">{p}</p>))}
          </div>
          <dl className="mt-8 grid grid-cols-3 gap-3">
            {WHOLESALE_INTRO.stats.map((s) => (
              <div key={s.id} data-testid={`wholesale-stat-${s.id}`} className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-4">
                <dd className="font-serif text-3xl font-semibold text-[var(--wf-maroon)]">{s.value}</dd>
                <dt className="mt-1 text-xs font-semibold uppercase tracking-[0.1em] text-[var(--wf-green-text)]">{s.label}</dt>
              </div>
            ))}
          </dl>
        </Reveal>
        <Reveal delay={0.1}>
          <ImageWithCredit src="/images/wf/vlai/vlai-3483.jpg" alt="A farmer sorts a pile of freshly cut greens at the edge of a field." className="aspect-[4/5] w-full rounded-sm shadow-[var(--wf-shadow)]" imgClassName="object-cover" />
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Channels ---------------- */
const ChannelsSection = () => (
  <section id="channels" aria-labelledby="wholesale-channels-heading" className="wf-section bg-white">
    <div className="wf-container">
      <Reveal className="max-w-2xl">
        <Kicker>Who we supply</Kicker>
        <h2 id="wholesale-channels-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">Wholesale channels</h2>
      </Reveal>
      <ul className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {WHOLESALE_CHANNELS.map((c, i) => {
          const Icon = CHANNEL_ICON[c.id] || Sprout;
          return (
            <Reveal key={c.id} delay={0.06 * i} as="li" className="h-full">
              <div data-testid={`wholesale-channel-${c.id}`} className="h-full rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[var(--wf-farm-green)] text-white"><Icon className="h-5 w-5" aria-hidden="true" /></span>
                <h3 className="mt-4 font-serif text-lg font-semibold text-[var(--wf-ink)]">{c.title}</h3>
                <p className="mt-2 text-sm leading-6 text-[var(--wf-soil)]">{c.body}</p>
              </div>
            </Reveal>
          );
        })}
      </ul>
    </div>
  </section>
);

/* ---------------- Contact form (MOCKED) ---------------- */
const ContactSection = () => {
  const [form, setForm] = useState({ business: "", contact: "", email: "", phone: "", buyerType: "", volume: "", crops: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.business.trim()) next.business = "Please enter your business name.";
    if (!form.contact.trim()) next.contact = "Please enter a contact name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = "Please enter a valid email address.";
    if (!form.buyerType) next.buyerType = "Please select a buyer type.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true);
    try {
      await api.post("/forms/wholesale", { ...form, email: form.email.trim() });
      setSent(true);
      toast.success("Inquiry sent", { description: "Our Wholesale Team will follow up within two business days." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("We couldn't send your inquiry", { description: typeof detail === "string" ? detail : "Please try again, or email sales@worldfarmers.org directly." });
    } finally {
      setSubmitting(false);
    }
  };

  const field = "min-h-11 rounded-lg border-[var(--wf-border)] bg-white focus-visible:ring-[var(--wf-maroon)]";
  const err = (k) => errors[k] && <p id={`wholesale-${k}-error`} role="alert" data-testid={`wholesale-${k}-error`} className="text-sm text-[var(--wf-maroon)]">{errors[k]}</p>;

  return (
    <section id="wholesale-contact" aria-labelledby="wholesale-contact-heading" className="wf-section scroll-mt-20 bg-[var(--wf-cream)]">
      <div className="wf-container">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-16">
          <Reveal>
            <Kicker>{WHOLESALE_CONTACT.eyebrow}</Kicker>
            <h2 id="wholesale-contact-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">{WHOLESALE_CONTACT.title}</h2>
            <p className="mt-4 text-base leading-7 text-[var(--wf-soil)]">{WHOLESALE_CONTACT.body}</p>
            <ul className="mt-6 space-y-3">
              {WHOLESALE_BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm leading-6 text-[var(--wf-ink)]">
                  <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[rgba(47,93,58,0.12)] text-[var(--wf-green-text)]"><Check className="h-3 w-3" aria-hidden="true" /></span>
                  {b}
                </li>
              ))}
            </ul>
            <p className="mt-8 flex items-center gap-2 text-sm text-[var(--wf-soil)]">
              <Phone className="h-4 w-4 text-[var(--wf-maroon)]" aria-hidden="true" />
              Prefer to call? <a href={WHOLESALE_CONTACT.phoneHref} className="font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline">{WHOLESALE_CONTACT.phone}</a>
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            {sent ? (
              <div data-testid="wholesale-form-success" role="status" className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-8 shadow-[var(--wf-shadow-sm)]">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--wf-farm-green)] text-white"><Check className="h-6 w-6" aria-hidden="true" /></span>
                <h3 className="mt-4 font-serif text-2xl font-semibold text-[var(--wf-ink)]">Thanks, {form.contact.split(" ")[0]}!</h3>
                <p className="mt-2 text-base leading-7 text-[var(--wf-soil)]">We’ve received your inquiry for <strong>{form.business}</strong>. Our Wholesale Team will follow up at {form.email} within two business days with current availability and pricing.</p>
                <p className="mt-4 text-xs text-muted-foreground">A copy of your inquiry has been sent to our Wholesale Team.</p>
                <button type="button" onClick={() => { setSent(false); setForm({ business: "", contact: "", email: "", phone: "", buyerType: "", volume: "", crops: "", message: "" }); }} data-testid="wholesale-form-reset" className="mt-6 text-sm font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">Send another inquiry</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="wholesale-form" className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-6 shadow-[var(--wf-shadow-sm)] sm:p-8">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="wh-business" className="text-sm font-semibold text-[var(--wf-ink)]">Business name</Label>
                    <Input id="wh-business" value={form.business} onChange={set("business")} aria-invalid={!!errors.business} aria-describedby={errors.business ? "wholesale-business-error" : undefined} data-testid="wholesale-business-input" className={field} />
                    {err("business")}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wh-contact" className="text-sm font-semibold text-[var(--wf-ink)]">Contact name</Label>
                    <Input id="wh-contact" value={form.contact} onChange={set("contact")} aria-invalid={!!errors.contact} aria-describedby={errors.contact ? "wholesale-contact-error" : undefined} data-testid="wholesale-contact-input" className={field} />
                    {err("contact")}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wh-email" className="text-sm font-semibold text-[var(--wf-ink)]">Email</Label>
                    <Input id="wh-email" type="email" inputMode="email" value={form.email} onChange={set("email")} aria-invalid={!!errors.email} aria-describedby={errors.email ? "wholesale-email-error" : undefined} data-testid="wholesale-email-input" className={field} />
                    {err("email")}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wh-phone" className="text-sm font-semibold text-[var(--wf-ink)]">Phone <span className="font-normal text-muted-foreground">(optional)</span></Label>
                    <Input id="wh-phone" type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} data-testid="wholesale-phone-input" className={field} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wh-type" className="text-sm font-semibold text-[var(--wf-ink)]">Buyer type</Label>
                    <Select value={form.buyerType} onValueChange={(v) => setForm((f) => ({ ...f, buyerType: v }))}>
                      <SelectTrigger id="wh-type" data-testid="wholesale-buyer-type-select" aria-invalid={!!errors.buyerType} className={cn(field, "w-full")}><SelectValue placeholder="Select one" /></SelectTrigger>
                      <SelectContent>{WHOLESALE_BUYER_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                    </Select>
                    {err("buyerType")}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wh-volume" className="text-sm font-semibold text-[var(--wf-ink)]">Estimated weekly volume <span className="font-normal text-muted-foreground">(optional)</span></Label>
                    <Input id="wh-volume" value={form.volume} onChange={set("volume")} placeholder="e.g. 20 cases of greens" data-testid="wholesale-volume-input" className={field} />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="wh-crops" className="text-sm font-semibold text-[var(--wf-ink)]">Crops of interest <span className="font-normal text-muted-foreground">(optional)</span></Label>
                    <Input id="wh-crops" value={form.crops} onChange={set("crops")} placeholder="Amaranth, Thai basil, okra, bok choy…" data-testid="wholesale-crops-input" className={field} />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="wh-message" className="text-sm font-semibold text-[var(--wf-ink)]">Message <span className="font-normal text-muted-foreground">(optional)</span></Label>
                    <Textarea id="wh-message" rows={4} value={form.message} onChange={set("message")} data-testid="wholesale-message-input" className="rounded-lg border-[var(--wf-border)] bg-white focus-visible:ring-[var(--wf-maroon)]" />
                  </div>
                </div>
                <WfButton type="submit" disabled={submitting} aria-busy={submitting} className="mt-6 w-full sm:w-auto sm:px-8" data-testid="wholesale-submit-button">
                  <Send className="h-4 w-4" aria-hidden="true" /> Send inquiry
                </WfButton>
                <p className="mt-3 text-xs text-muted-foreground">Demo form — submissions are not yet delivered to staff.</p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Page ---------------- */
export default function WholesalePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <PageHero {...WHOLESALE_HERO} testid="wholesale-hero" />
        <IntroSection />
        <ChannelsSection />
        <SeasonalityChart
          id="seasonal-lineup"
          rows={WHOLESALE_SEASONALITY}
          eyebrow="Seasonal crop lineup"
          title="What’s available, and when"
          intro="Pick a month to see which crops our farmers typically have in volume. Availability shifts week to week — contact our Wholesale Team for a current list."
          note="Typical availability from the Flats Mentor Farm in Lancaster, MA. Volumes and varieties vary by season and grower."
          testidPrefix="wholesale"
          className="bg-[var(--wf-cream)]"
        />
        <ContactSection />
      </main>
      <SiteFooter />
    </>
  );
}
